import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "hooks/useAuth";
import { prepareShopifyInstall } from "services/api";

function toShopDomain(v: string) {
  const d = String(v || "").trim().toLowerCase();
  if (!d) return "";
  return d.endsWith(".myshopify.com") ? d : `${d}.myshopify.com`;
}

const s = {
  wrapper: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, #dbeafe 0%, #eff6ff 50%, #dbeafe 100%)",
  } as React.CSSProperties,
  card: {
    background: "#fff",
    borderRadius: 20,
    padding: "40px 36px",
    width: "100%",
    maxWidth: 420,
    boxShadow: "0 8px 32px rgba(0,0,0,0.10)",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: 20,
  },
  logo: { height: 52, marginBottom: 4 },
  tagline: { color: "#64748b", fontSize: 14, textAlign: "center" as const, margin: 0 },
  shopBadge: {
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 13,
    color: "#166534",
    textAlign: "center" as const,
    width: "100%",
  },
  hint: { color: "#64748b", fontSize: 14, textAlign: "center" as const, margin: 0 },
  form: { width: "100%", display: "flex", flexDirection: "column" as const, gap: 14 },
  fieldLabel: { display: "block", fontSize: 13, color: "#374151", marginBottom: 4 },
  input: {
    width: "100%",
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    fontSize: 14,
    outline: "none",
    boxSizing: "border-box" as const,
  },
  btn: {
    width: "100%",
    padding: "12px 0",
    background: "#4f46e5",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
    marginTop: 4,
  },
  btnDisabled: { opacity: 0.6, cursor: "not-allowed" },
  error: {
    color: "#b91c1c",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: "8px 12px",
    fontSize: 13,
    width: "100%",
    textAlign: "center" as const,
  },
};

export const ShopifyInstall: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading, login } = useAuth();

  const shopFromUrl = toShopDomain(searchParams.get("shop") || "");

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [step, setStep] = useState<"login" | "connect">("login");
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      setStep("connect");
    }
  }, [authLoading, isAuthenticated]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    setLoading(true);
    try {
      await login(email, senha);
      setStep("connect");
    } catch (err: any) {
      setErro(err?.response?.data?.erro || err?.message || "Falha ao fazer login.");
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    if (!shopFromUrl) {
      setErro("Domínio da loja não encontrado. Reinstale o app pela Shopify.");
      return;
    }
    setErro(null);
    setLoading(true);
    try {
      const { data } = await prepareShopifyInstall(shopFromUrl);
      // Usa window.top para sair do iframe do Shopify Admin caso esteja embutido
      const target = (() => {
        try { return window.top && window.top !== window.self ? window.top : window; }
        catch { return window; }
      })();
      target.location.href = data.authUrl;
    } catch (err: any) {
      setErro(err?.response?.data?.erro || err?.message || "Falha ao iniciar conexão.");
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div style={s.wrapper}>
        <div style={s.card}>
          <p style={s.hint}>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={s.wrapper}>
      <div style={s.card}>
        <img src="/images/intrex_logo.png" alt="Intrex" style={s.logo} />
        <p style={s.tagline}>Conecte sua loja Shopify à Intrex</p>

        {shopFromUrl && (
          <div style={s.shopBadge}>
            Loja: <strong>{shopFromUrl}</strong>
          </div>
        )}

        {step === "login" && (
          <form onSubmit={handleLogin} style={s.form}>
            <p style={s.hint}>
              Faça login com sua conta Intrex para concluir a instalação.
            </p>
            <div>
              <label style={s.fieldLabel}>Email</label>
              <input
                type="email"
                placeholder="você@exemplo.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                style={s.input}
              />
            </div>
            <div>
              <label style={s.fieldLabel}>Senha</label>
              <input
                type="password"
                placeholder="••••••••"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                disabled={loading}
                style={s.input}
              />
            </div>
            {erro && <div style={s.error}>{erro}</div>}
            <button
              type="submit"
              disabled={loading}
              style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
            >
              {loading ? "Entrando..." : "Entrar"}
            </button>
          </form>
        )}

        {step === "connect" && (
          <>
            <p style={s.hint}>
              Sua conta Intrex está pronta. Clique abaixo para autorizar a loja e ativar o frete automático.
            </p>
            {erro && <div style={s.error}>{erro}</div>}
            <button
              onClick={handleConnect}
              disabled={loading}
              style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}
            >
              {loading ? "Conectando..." : "Conectar loja Shopify"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};
